<template>
  <a-card>
    <v-row>
      <v-col><h3>General</h3></v-col>
      <v-col v-if="data.id" align="right">
        <div class="caption">Updated by {{ data.updated_by }} : {{ formatDateTime(data.updated_at) }}</div>
      </v-col>
    </v-row>
    <div class="booking-title">Quote</div>
    <div class="quote-info-block">
      <v-row>
        <v-col cols="12"><ClientAutocomplete v-model="data.client" /></v-col>
      </v-row>
      <v-row>
        <v-col cols="9">
          <div class="d-flex align-center">
            <a-text-field :value="data.title" label="Title" @input="onTitleInput" />
            <v-tooltip top left>
              <template v-slot:activator="{ on }">
                <v-btn color="grey" v-on="on" icon small>
                  <v-icon small>mdi-information-outline</v-icon>
                </v-btn>
              </template>
              <span>Quote title is used to identify title of quote</span>
            </v-tooltip>
          </div>
        </v-col>
        <v-col cols="3">
          <div class="d-flex align-center">
            <elwrapper label="Version">
              <v-text-field
                v-model="data.version"
                type="number"
                outlined
                hide-details
                dense
                @keydown="(e) => onKeyDown(e)"
              />
            </elwrapper>
            <v-tooltip top left>
              <template v-slot:activator="{ on }">
                <v-btn color="grey" v-on="on" icon small>
                  <v-icon small>mdi-information-outline</v-icon>
                </v-btn>
              </template>
              <span>Quote version is used to track revisions of the same quote</span>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <div class="d-flex align-center">
            <a-text-field :value="folderDisplayValue" label="Quote Folder" @input="onFolderInput" />
            <v-tooltip top left>
              <template v-slot:activator="{ on }">
                <v-btn color="grey" v-on="on" icon small>
                  <v-icon small>mdi-information-outline</v-icon>
                </v-btn>
              </template>
              <span>Quote folder is used to group quotes</span>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <div class="d-flex align-center">
            <a-text-field v-model="data.reference" label="Reference" />
            <v-tooltip top left>
              <template v-slot:activator="{ on }">
                <v-btn color="grey" v-on="on" icon small>
                  <v-icon small>mdi-information-outline</v-icon>
                </v-btn>
              </template>
              <span>Quote reference is used to identify reference of quote</span>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <div class="quote-info-stacked-field">
            <div class="quote-info-stacked-label">Accommodation categories (quote filter)</div>
            <f-dropdown multiple :items="accommodationTypes" v-model="data.accommodations" />
            <div class="caption mt-1">Hotel classes used to filter and find this quote.</div>
          </div>
        </v-col>
      </v-row>
    </div>
    <div class="booking-title">Tour Information</div>
    <div class="period-block" :class="{ 'period-block--active': isPrimaryPreviewSelected }">
      <div class="period-header">
        <div class="period-header__title">
          <elwrapper label="Period name">
            <v-text-field
              dense
              outlined
              hide-details
              placeholder="Period 1"
              :value="data.periodName"
              @input="updatePrimaryPeriodName"
            ></v-text-field>
          </elwrapper>
          <div class="caption mt-1">Used in the Excel export and price preview.</div>
        </div>
        <div class="period-header__actions">
          <v-chip v-if="isPrimaryPreviewSelected" small color="primary" text-color="white">
            Prices shown below
          </v-chip>
          <v-btn
            small
            depressed
            class="period-show-prices-btn"
            :color="isPrimaryPreviewSelected ? 'primary' : 'grey lighten-3'"
            :class="{ 'period-show-prices-btn--inactive': !isPrimaryPreviewSelected }"
            @click="$emit('update:selectedPeriodPreview', 'primary')"
          >
            {{ isPrimaryPreviewSelected ? "Selected" : "Show prices below" }}
          </v-btn>
        </div>
      </div>
      <div class="period-fields-row">
        <div class="period-field-col">
          <elwrapper label="Arrival">
            <v-text-field :value="displayFrom" outlined hide-details dense readonly @click="showPeriodDialog" />
          </elwrapper>
        </div>
        <div class="period-field-col">
          <elwrapper label="City" smlabel>
            <v-autocomplete dense outlined hide-details :items="cities" v-model="data.cityStart"></v-autocomplete>
          </elwrapper>
        </div>
        <div class="period-field-col">
          <elwrapper label="Nights">
            <v-text-field :value="displayNights" outlined hide-details dense readonly @click="showPeriodDialog" />
          </elwrapper>
        </div>
      </div>
      <div class="period-fields-row">
        <div class="period-field-col">
          <elwrapper label="Departure">
            <v-text-field :value="displayTo" outlined hide-details dense readonly @click="showPeriodDialog" />
          </elwrapper>
        </div>
        <div class="period-field-col">
          <elwrapper label="City" smlabel>
            <v-autocomplete dense outlined hide-details :items="cities" v-model="data.cityEnd"></v-autocomplete>
          </elwrapper>
        </div>
      </div>
    </div>

    <div
      v-for="(comparisonPeriod, index) in comparisonPeriods"
      :key="index"
      class="period-block"
      :class="{ 'period-block--active': isComparisonPreviewSelected(index) }"
    >
      <div class="period-header">
        <div class="period-header__title">
          <elwrapper :label="`Period ${index + 2} name`">
            <v-text-field
              dense
              outlined
              hide-details
              :placeholder="`Period ${index + 2}`"
              :value="comparisonPeriod.periodName"
              @input="updateComparisonPeriodName(index, $event)"
            ></v-text-field>
          </elwrapper>
        </div>
        <div class="period-header__actions">
          <v-chip v-if="isComparisonPreviewSelected(index)" small color="primary" text-color="white">
            Prices shown below
          </v-chip>
          <v-btn
            small
            depressed
            class="period-show-prices-btn"
            :color="isComparisonPreviewSelected(index) ? 'primary' : 'grey lighten-3'"
            :class="{ 'period-show-prices-btn--inactive': !isComparisonPreviewSelected(index) }"
            @click="$emit('update:selectedPeriodPreview', `comparison-${index}`)"
          >
            {{ isComparisonPreviewSelected(index) ? "Selected" : "Show prices below" }}
          </v-btn>
          <icon-btn icon="delete" @onclick="removeComparisonPeriod(index)" />
        </div>
      </div>
      <div class="period-fields-row">
        <div class="period-field-col">
          <elwrapper label="Arrival">
            <v-text-field
              :value="displayComparisonStart(comparisonPeriod)"
              outlined
              hide-details
              dense
              readonly
              @click="showComparisonPeriodDialog(index)"
            />
          </elwrapper>
        </div>
        <div class="period-field-col">
          <elwrapper label="City" smlabel>
            <v-autocomplete
              dense
              outlined
              hide-details
              :items="cities"
              v-model="comparisonPeriod.cityStart"
              @input="updateComparisonPeriod(index, comparisonPeriod)"
            ></v-autocomplete>
          </elwrapper>
        </div>
      </div>
      <div class="period-fields-row">
        <div class="period-field-col">
          <elwrapper label="Departure">
            <v-text-field
              :value="displayComparisonEnd(comparisonPeriod)"
              outlined
              hide-details
              dense
              readonly
              @click="showComparisonPeriodDialog(index)"
            />
          </elwrapper>
        </div>
        <div class="period-field-col">
          <elwrapper label="City" smlabel>
            <v-autocomplete
              dense
              outlined
              hide-details
              :items="cities"
              v-model="comparisonPeriod.cityEnd"
              @input="updateComparisonPeriod(index, comparisonPeriod)"
            ></v-autocomplete>
          </elwrapper>
        </div>
      </div>
    </div>

    <div v-if="canAddComparisonPeriod" class="quote-section-footer">
      <v-btn small text color="primary" class="quote-section-add-line" @click="addComparisonPeriod">
        <v-icon left small>mdi-plus</v-icon>
        Add period
      </v-btn>
    </div>

    <PeriodRangeDialog v-model="isDialogShowed" :data="data" @update="updatePeriod" />
    <RemoveQuoteDaysDialog
      v-model="isRemoveDaysDialogShowed"
      :days="removeDaysOptions"
      :daysToRemove="pendingDaysToRemove"
      @confirm="onRemoveDaysConfirm"
      @cancel="onRemoveDaysCancel"
    />
    <ComparisonPeriodDialog
      v-model="isComparisonDialogShowed"
      :data="activeComparisonPeriod"
      :nights="data.nights"
      :periodNumber="activeComparisonPeriodIndex + 2"
      @update="updateActiveComparisonPeriod"
    />
  </a-card>
</template>

<script>
import MONTHS from "@/config/MONTHS";
import PeriodRangeDialog from "./PeriodRangeDialog";
import ComparisonPeriodDialog from "./ComparisonPeriodDialog";
import RemoveQuoteDaysDialog from "./RemoveQuoteDaysDialog";
import { ClientAutocomplete } from "@/modules/clients";
import { SupplierHotelMeta } from "../../../config/fields/suppliers";
import quoteUtils from "@/modules/quotes/components/utils";
import format from "@/services/format";
export default {
  components: {
    ClientAutocomplete,
    PeriodRangeDialog,
    ComparisonPeriodDialog,
    RemoveQuoteDaysDialog,
  },
  props: {
    value: Object,
    supplierProductData: Object,
    selectedPeriodPreview: {
      type: String,
      default: "primary",
    },
  },
  data() {
    return {
      isDialogShowed: false,
      isComparisonDialogShowed: false,
      isRemoveDaysDialogShowed: false,
      activeComparisonPeriodIndex: null,
      cities: [],
      folderManuallyEdited: false,
      lastAutoFolder: "",
      pendingPeriodUpdate: null,
    };
  },
  computed: {
    data: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit("input", newValue);
      },
    },
    accommodationTypes() {
      return SupplierHotelMeta.hotel_class.source.reverse();
    },
    displayFrom() {
      const p = this.data.period || {};
      return this.displayDate(p.startYear, p.startMonth, p.startDay);
    },
    displayTo() {
      const p = this.data.period || {};
      return this.displayDate(p.endYear, p.endMonth, p.endDay);
    },
    displayNights() {
      return this.value.nights;
    },
    suggestedFolder() {
      return quoteUtils.buildQuoteFolderName(this.data?.title, this.data?.nights);
    },
    folderDisplayValue() {
      return this.data?.folder ?? "";
    },
    comparisonPeriods() {
      return (this.data.comparisonPeriods || []).slice(0, quoteUtils.MAX_COMPARISON_PERIODS);
    },
    canAddComparisonPeriod() {
      return this.comparisonPeriods.length < quoteUtils.MAX_COMPARISON_PERIODS;
    },
    activeComparisonPeriod() {
      if (this.activeComparisonPeriodIndex === null) return this.createEmptyComparisonPeriod();
      return this.comparisonPeriods[this.activeComparisonPeriodIndex] || this.createEmptyComparisonPeriod();
    },
    isPrimaryPreviewSelected() {
      return this.selectedPeriodPreview === "primary";
    },
    removeDaysOptions() {
      const pending = this.pendingPeriodUpdate;
      if (!pending) return [];

      const period = pending.previousPeriod?.period || this.data.period || {};
      return (pending.previousCosts || []).map((cost, index) => ({
        label: this.formatDayRemovalLabel(cost, index, period),
      }));
    },
    pendingDaysToRemove() {
      return this.pendingPeriodUpdate?.daysToRemove || 0;
    },
  },
  watch: {
    "value.id": {
      immediate: true,
      handler() {
        this.resetFolderOverrideState();
      },
    },
    "value.title"() {
      this.syncFolderFromSuggestion();
    },
    "value.nights"() {
      this.syncFolderFromSuggestion();
    },
  },
  methods: {
    onQuotePeriodInput(v) {
      const numberOfNights = Number(v.nights);
      if (numberOfNights >= 0) {
        this.tempCosts = this.data.costs;
        this.updateCosts(numberOfNights);
      }
      this.updateTotalCosts();
    },
    updateCosts(nights) {
      // Update the row of data for variable costs based on the number of nights
      this.data.costs = [];

      const days = nights + 1;
      for (let i = 0; i < days; i++) {
        const existingCost = this.tempCosts && this.tempCosts[i] !== undefined ? this.tempCosts[i] : null;
        if (existingCost !== null && existingCost !== undefined) {
          this.data.costs[i] = {
            breakfast: false,
            lunch: false,
            dinner: false,
            ...existingCost,
          };
        } else {
          this.data.costs.push({
            index: i,
            items: [],
            breakfast: false,
            lunch: false,
            dinner: false,
          });
        }
      }
    },
    updateTotalCosts() {
      const { costs, logisticsCosts } = this.data;

      costs.forEach((cost, index) => {
        cost.items.forEach((item) => {
          if (item.isProductEditableCost) return;

          const supplierId = item.supplier?.value;
          const productId = item.product?.value;
          const product = this.supplierProductData[supplierId]?.products[productId];

          if (!supplierId || !productId || !product) return;

          const period = this.data.period;
          const previousDayHotelId = this.getPreviousDayHotelId(index);
          const productCost = quoteUtils.getProductCost(
            this.$store.getters["AccountStore/environment"],
            this.supplierProductData,
            product,
            period.startYear,
            period,
            index,
            previousDayHotelId
          );

          item.productCost = {
            type: productCost.pricingType,
            currency: productCost.currency,
            value: productCost.value,
            valueIncludesPorterage: productCost.valueIncludesPorterage,
            porterage: productCost.porterage,
            confirm: productCost.confirm,
            rateKind: productCost.rateKind,
          };
          item.productType = product.product_type;
          item.costType = productCost.costType;
          quoteUtils.applyShowInProgramForProductType(item);
        });
      });

      logisticsCosts.forEach((item) => {
        if (item.isProductEditableCost) return;

        const supplierId = item.supplier?.value;
        const productId = item.product?.value;
        const product = this.supplierProductData[supplierId]?.products[productId];

        if (!supplierId || !productId || !product) return;

        const period = this.data.period;
        const productCost = quoteUtils.getProductCost(
          this.$store.getters["AccountStore/environment"],
          this.supplierProductData,
          product,
          period.startYear,
          period,
          null,
          null
        );

        item.productCost = {
          type: productCost.pricingType,
          currency: productCost.currency,
          value: productCost.value,
          valueIncludesPorterage: productCost.valueIncludesPorterage,
          porterage: productCost.porterage,
          confirm: productCost.confirm,
          rateKind: productCost.rateKind,
        };
        item.productType = product.product_type;
        quoteUtils.applyShowInProgramForProductType(item);
      });
    },
    getPreviousDayHotelId(dayIndex) {
      if (dayIndex === 0) return null;

      const perviousDayIndex = dayIndex - 1;
      const previousDayItems = this.data.costs[perviousDayIndex]?.items || [];
      if (previousDayItems.length === 0) return null;

      const room = previousDayItems.find((item) => item.productType === "ROOM");
      if (!room?.supplier?.value) return null;

      return room.supplier.value;
    },
    onKeyDown(e) {
      if (e.key === "." || e.key === "-" || e.key === "+") e.preventDefault();
    },
    resetFolderOverrideState() {
      const folder = String(this.data?.folder || "").trim();
      const suggested = this.suggestedFolder;

      if (!folder) {
        this.folderManuallyEdited = false;
        this.lastAutoFolder = "";
        this.syncFolderFromSuggestion();
        return;
      }

      this.folderManuallyEdited = !suggested || folder !== suggested;
      this.lastAutoFolder = this.folderManuallyEdited ? "" : folder;
    },
    writeFolder(folder) {
      if (!this.data) return;
      if (this.data.folder === folder) return;
      this.$set(this.data, "folder", folder);
      this.$emit("input", this.data);
    },
    syncFolderFromSuggestion() {
      if (this.folderManuallyEdited) return;

      const suggested = this.suggestedFolder;
      if (!suggested) return;

      const currentFolder = String(this.data?.folder || "").trim();
      if (currentFolder && currentFolder !== this.lastAutoFolder) {
        this.folderManuallyEdited = true;
        return;
      }

      this.lastAutoFolder = suggested;
      this.writeFolder(suggested);
    },
    onFolderInput(value) {
      const next = value == null ? "" : String(value);
      const trimmed = next.trim();

      if (!trimmed) {
        this.folderManuallyEdited = false;
        this.lastAutoFolder = "";
        this.writeFolder("");
        this.syncFolderFromSuggestion();
        return;
      }

      this.writeFolder(next);
      this.folderManuallyEdited = trimmed !== this.suggestedFolder;
      if (!this.folderManuallyEdited) {
        this.lastAutoFolder = trimmed;
      }
    },
    onTitleInput(value) {
      const next = value == null ? null : String(value);
      if (!this.data) return;
      this.$set(this.data, "title", next);
      this.$emit("input", this.data);
      this.syncFolderFromSuggestion();
    },
    fetchCities() {
      return this.$store
        .dispatch("DatabaseStore/getCityList")
        .then((cities) => {
          this.cities = cities;
        })
        .catch((err) => this.$root.$error(err));
    },
    showPeriodDialog() {
      this.isDialogShowed = true;
    },
    createEmptyComparisonPeriod() {
      return {
        periodName: null,
        period: {
          startYear: null,
          startMonth: null,
          startDay: null,
          endYear: null,
          endMonth: null,
          endDay: null,
        },
        cityStart: null,
        cityEnd: null,
        margin: this.clonePrimaryMargin(),
        costOverrides: {},
        logisticsCostOverrides: {},
        supplementTripleOverride: null,
        supplementQuadOverride: null,
      };
    },
    clonePrimaryMargin() {
      const source = this.data.margin || {};
      return {
        status: source.status || "fixed",
        percentFixed: source.percentFixed ?? 0,
        percentDynamic: [...(source.percentDynamic || [])],
        perPax: [...(source.perPax || [])],
        perGroup: [...(source.perGroup || [])],
      };
    },
    addComparisonPeriod() {
      if (!this.data.comparisonPeriods) this.$set(this.data, "comparisonPeriods", []);
      if (this.data.comparisonPeriods.length >= quoteUtils.MAX_COMPARISON_PERIODS) return;
      this.data.comparisonPeriods.push(this.createComparisonPeriodFromPeriodOne());
    },
    removeComparisonPeriod(index) {
      this.data.comparisonPeriods.splice(index, 1);
    },
    showComparisonPeriodDialog(index) {
      this.activeComparisonPeriodIndex = index;
      this.isComparisonDialogShowed = true;
    },
    updateActiveComparisonPeriod(period) {
      if (this.activeComparisonPeriodIndex === null) return;
      this.updateComparisonPeriod(this.activeComparisonPeriodIndex, period);
    },
    updateComparisonPeriodName(index, periodName) {
      const comparisonPeriod = this.data.comparisonPeriods?.[index];
      if (!comparisonPeriod) return;

      this.$set(this.data.comparisonPeriods, index, {
        ...comparisonPeriod,
        periodName,
      });
    },
    updatePrimaryPeriodName(periodName) {
      this.$set(this.data, "periodName", periodName);
    },
    updateComparisonPeriod(index, period) {
      const currentPeriod = this.data.comparisonPeriods?.[index] || this.createEmptyComparisonPeriod();
      const updatedPeriod = this.calculateComparisonEndDate({
        ...currentPeriod,
        ...period,
        period: { ...(period.period || {}) },
        margin: period.margin || currentPeriod.margin || this.clonePrimaryMargin(),
        costOverrides: currentPeriod.costOverrides || {},
        logisticsCostOverrides: currentPeriod.logisticsCostOverrides || {},
        supplementTripleOverride: currentPeriod.supplementTripleOverride ?? null,
        supplementQuadOverride: currentPeriod.supplementQuadOverride ?? null,
      });
      updatedPeriod.periodName = this.getNextPeriodName(currentPeriod, updatedPeriod.period);
      this.$set(this.data.comparisonPeriods, index, updatedPeriod);
    },
    createComparisonPeriodFromPeriodOne() {
      const comparisonPeriod = this.calculateComparisonEndDate({
        periodName: null,
        period: { ...(this.data.period || {}) },
        cityStart: this.data.cityStart,
        cityEnd: this.data.cityEnd,
        margin: this.clonePrimaryMargin(),
        costOverrides: {},
        logisticsCostOverrides: {},
        supplementTripleOverride: null,
        supplementQuadOverride: null,
      });
      comparisonPeriod.periodName = this.getPeriodNameFromPeriod(comparisonPeriod.period);
      return comparisonPeriod;
    },
    recalculateComparisonPeriods() {
      if (!this.data.comparisonPeriods) this.$set(this.data, "comparisonPeriods", []);
      this.data.comparisonPeriods.slice(0, quoteUtils.MAX_COMPARISON_PERIODS).forEach((period, index) => {
        const recalculatedPeriod = this.calculateComparisonEndDate({
          ...period,
          period: { ...(period.period || {}) },
          margin: period.margin || this.clonePrimaryMargin(),
          costOverrides: period.costOverrides || {},
          logisticsCostOverrides: period.logisticsCostOverrides || {},
          supplementTripleOverride: period.supplementTripleOverride ?? null,
          supplementQuadOverride: period.supplementQuadOverride ?? null,
        });
        recalculatedPeriod.periodName = this.getNextPeriodName(period, recalculatedPeriod.period);
        this.$set(
          this.data.comparisonPeriods,
          index,
          recalculatedPeriod
        );
      });
      if (this.data.comparisonPeriods.length > quoteUtils.MAX_COMPARISON_PERIODS) {
        this.data.comparisonPeriods.splice(quoteUtils.MAX_COMPARISON_PERIODS);
      }
    },
    calculateComparisonEndDate(comparisonPeriod) {
      const period = comparisonPeriod.period || {};
      const { startYear, startMonth, startDay } = period;
      const nights = this.data.nights;

      if (!startYear || !startMonth || !startDay || nights === null || nights === undefined || nights === "") {
        return {
          ...comparisonPeriod,
          period: { ...period, endYear: null, endMonth: null, endDay: null },
        };
      }

      const startDate = new Date(Number(startYear), Number(startMonth) - 1, Number(startDay));
      if (Number.isNaN(startDate.getTime())) return comparisonPeriod;

      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + Number(nights));

      return {
        ...comparisonPeriod,
        period: {
          ...period,
          endYear: endDate.getFullYear().toString(),
          endMonth: endDate.getMonth() + 1,
          endDay: endDate.getDate(),
        },
      };
    },
    displayComparisonStart(comparisonPeriod) {
      const period = comparisonPeriod.period || {};
      return this.displayDate(period.startYear, period.startMonth, period.startDay);
    },
    displayComparisonEnd(comparisonPeriod) {
      const period = comparisonPeriod.period || {};
      return this.displayDate(period.endYear, period.endMonth, period.endDay);
    },
    displayDate(year, month, day) {
      const parts = [];
      if (year) parts.push(year);
      if (month) parts.push(MONTHS[month - 1].label);
      if (day) parts.push(day);
      return parts.join(" ");
    },
    getFrenchArrivalLabel(period) {
      return quoteUtils.getFrenchArrivalLabel(period);
    },
    getPeriodNameFromPeriod(period) {
      return this.getFrenchArrivalLabel(period);
    },
    getNextPeriodName(previousPeriod, nextPeriod) {
      const previousManualName = previousPeriod?.periodName;
      const previousAutoName = this.getPeriodNameFromPeriod(previousPeriod?.period || {});
      if (!previousManualName || previousManualName === previousAutoName) {
        return this.getPeriodNameFromPeriod(nextPeriod);
      }
      return previousManualName;
    },
    isComparisonPreviewSelected(index) {
      return this.selectedPeriodPreview === `comparison-${index}`;
    },
    updatePeriod(data) {
      const previousPeriod = { periodName: this.data.periodName, period: { ...(this.data.period || {}) } };
      const previousCosts = [...(this.data.costs || [])];
      const newNights = Number(data.nights);
      const oldDays = previousCosts.length;
      const newDays = Number.isFinite(newNights) && newNights >= 0 ? newNights + 1 : 0;
      const daysToRemove = oldDays - newDays;

      if (daysToRemove > 0 && oldDays > 0) {
        this.pendingPeriodUpdate = {
          data,
          previousPeriod,
          previousCosts,
          daysToRemove,
        };
        this.isRemoveDaysDialogShowed = true;
        return;
      }

      this.applyPeriodUpdate(data, previousPeriod, previousCosts);
    },
    applyPeriodUpdate(data, previousPeriod, previousCosts, removedIndexes = null) {
      this.data.period = data.period;
      this.data.nights = data.nights;
      this.$set(this.data, "periodName", this.getNextPeriodName(previousPeriod, data.period));
      this.recalculateComparisonPeriods();

      if (removedIndexes?.length) {
        const removeSet = new Set(removedIndexes);
        this.tempCosts = previousCosts
          .filter((_, index) => !removeSet.has(index))
          .map((cost, index) => ({
            ...cost,
            index,
          }));
      } else {
        this.tempCosts = previousCosts;
      }

      this.updateCosts(Number(data.nights));
      this.updateTotalCosts();
      this.syncFolderFromSuggestion();
      this.$emit("input", this.data);
    },
    formatDayRemovalLabel(cost, index, period) {
      const dayLabel = this.formatDayWithDate(period, index);
      const cities = [cost?.cityStart, cost?.cityEnd].filter(Boolean).join(" → ");
      const itemCount = Array.isArray(cost?.items) ? cost.items.length : 0;
      const parts = [dayLabel];
      if (cities) parts.push(cities);
      if (itemCount > 0) parts.push(`${itemCount} product${itemCount > 1 ? "s" : ""}`);
      return parts.join(" • ");
    },
    formatDayWithDate(period, index) {
      const { startYear, startMonth, startDay } = period || {};

      if (startYear == null || startMonth == null || startDay == null) {
        return `Day ${index + 1}`;
      }

      const startDate = new Date(Number(startYear), Number(startMonth) - 1, Number(startDay));
      if (Number.isNaN(startDate.getTime())) return `Day ${index + 1}`;

      const dayDate = new Date(startDate);
      dayDate.setDate(startDate.getDate() + index);

      const formattedDate = dayDate.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      return `Day ${index + 1} ${formattedDate}`;
    },
    onRemoveDaysConfirm(removedIndexes) {
      const pending = this.pendingPeriodUpdate;
      if (!pending) return;

      this.applyPeriodUpdate(pending.data, pending.previousPeriod, pending.previousCosts, removedIndexes);
      this.pendingPeriodUpdate = null;
      this.isRemoveDaysDialogShowed = false;
    },
    onRemoveDaysCancel() {
      this.pendingPeriodUpdate = null;
      this.isRemoveDaysDialogShowed = false;
    },
    formatDateTime(date) {
      return format.formatDateTime(date);
    },
  },
  mounted() {
    this.fetchCities();
  },
};
</script>

<style scoped>
.quote-info-block,
.period-block {
  display: block;
  width: 53rem;
  max-width: 100%;
  box-sizing: border-box;
}

.quote-info-block {
  margin-bottom: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: #fafafa;
}

.quote-info-block .d-flex > *:first-child {
  flex: 1 1 auto;
  min-width: 0;
}

.quote-info-stacked-field {
  width: 100%;
}

.quote-info-stacked-label {
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.25;
  margin-bottom: 4px;
  color: rgba(0, 0, 0, 0.87);
}

.period-block {
  margin-bottom: 1rem;
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: #fafafa;
}

.period-block--active {
  border-color: #1976d2;
  background: #f5f9ff;
}

.period-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.period-header__title {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 32rem;
}

.period-header__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
  padding-top: 1.35rem;
}

.period-fields-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px 12px;
  margin-bottom: 4px;
}

.period-field-col {
  flex: 0 0 17rem;
  width: 17rem;
  min-width: 17rem;
  max-width: 17rem;
  box-sizing: border-box;
}

.quote-section-footer {
  width: 53rem;
  max-width: 100%;
  padding-top: 2px;
  margin-bottom: 1rem;
  box-sizing: border-box;
}

.quote-section-add-line {
  text-transform: none !important;
  letter-spacing: 0 !important;
  padding-left: 0 !important;
}

.period-show-prices-btn {
  text-transform: none;
  letter-spacing: normal;
}

.period-show-prices-btn--inactive {
  color: #455a64 !important;
}
</style>
